import { Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ReactiveFormsModule } from '@angular/forms';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { Subject, catchError, debounceTime, distinctUntilChanged, map, of, switchMap } from 'rxjs';

import { SAVING_ARENA_API_CONFIG } from '../../core/api/api.config';
import {
  ApiResponse,
  FrontendDealCategoryListItem,
  FrontendSearchResults,
  FrontendStore,
} from '../../core/api/api.models';
import { SavingArenaApiService } from '../../core/api/saving-arena-api.service';
import { FeaturedEventService } from '../../core/events/featured-event.service';
import { NewsletterSignup } from '../../core/newsletter/newsletter-signup';
import { PreconnectService } from '../../core/perf/preconnect.service';
import { AiChatPanel } from '../../shared/ai-chat-panel/ai-chat-panel';
import { ConsentService } from '../../core/consent/consent.service';
import { CookieBanner } from '../../shared/cookie-banner/cookie-banner';
import { StoreLogo } from '../../shared/store-logo/store-logo';

interface StoreSearchResult {
  name: string;
  path: string;
  logoUrl: string;
}

/**
 * One group in the header's search dropdown.
 *
 * The search used to return stores alone. It now also returns categories and the collections
 * behind them, so the results are grouped rather than run together — "Fashion" the category and
 * "Fashion" the collection are different pages, and a flat list would give a searcher no way to
 * tell which one they were about to open.
 */
interface SearchResultGroup {
  label: string;
  /** Shown under each result so the destination is legible: "Coupons", "Products", "Store". */
  kind: string;
  results: StoreSearchResult[];
}

/** One footer column entry — `path` is always a route that exists. */
interface FooterLink {
  label: string;
  path: string;
}

/**
 * One entry in the header's Collections menu — a category with a `/collection/:slug` page.
 *
 * The API only returns categories that actually qualify (enough active products), so every entry
 * here resolves; nothing needs filtering client-side.
 */
interface DealCollectionLink {
  label: string;
  path: string;
  productCount: number;
  imageUrl: string;
}

interface FooterGroup {
  label: string;
  links: FooterLink[];
}

const SEARCH_MIN_LENGTH = 2;
/**
 * Rows per group in the header typeahead.
 *
 * Six stores and three each of categories and collections is twelve rows plus three headings —
 * enough that the answer is usually on screen without scrolling, and short enough that the panel
 * does not run off the bottom of the page. It used to show eight, four and four, which reached
 * sixteen rows and left the last group hanging off the screen.
 */
const SEARCH_RESULT_LIMIT = 6;
const SEARCH_GROUP_LIMIT = 3;

/** Rows in the footer's Popular Stores column — keeps it the same height as the other columns. */
const FOOTER_STORE_LIMIT = 6;

/** Entries in the header's Collections menu. Comfortably more than the ~9 that qualify today. */
const DEAL_COLLECTION_LIMIT = 24;

@Component({
  selector: 'app-main-layout',
  imports: [
    CookieBanner,
    RouterOutlet,
    RouterLink,
    ReactiveFormsModule,
    AiChatPanel,
    StoreLogo,
  ],
  providers: [NewsletterSignup],
  templateUrl: './main-layout.html',
  styleUrl: './main-layout.scss',
})
export class MainLayout {
  private readonly router = inject(Router);
  /** Footer newsletter form. Addresses land in the admin panel under Newsletter Subscribers. */
  protected readonly signup = inject(NewsletterSignup);
  private readonly api = inject(SavingArenaApiService);
  private readonly apiConfig = inject(SAVING_ARENA_API_CONFIG);
  // Shared with the BIG SALE banner so both promo links always name the same event.
  private readonly featuredEvent = inject(FeaturedEventService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly searchInput$ = new Subject<string>();

  protected readonly searchTerm = signal('');
  protected readonly searchGroups = signal<SearchResultGroup[]>([]);
  /** Every result across the groups, in display order — what Enter picks from. */
  protected readonly searchResults = computed(() =>
    this.searchGroups().flatMap((group) => group.results),
  );
  protected readonly isSearching = signal(false);
  protected readonly isSearchOpen = signal(false);

  /**
   * The phone nav drawer.
   *
   * The main navigation is `xl:flex`, so below 1280px there was no route to Categories, Top Stores,
   * Shop, Blog or Coupons at all — a phone only ever saw the three utility links in the header.
   */
  protected readonly isMobileMenuOpen = signal(false);

  protected toggleMobileMenu(): void {
    this.isMobileMenuOpen.update((open) => !open);
  }

  /** Called on every link inside the drawer: the route changes under it, so it has to close. */
  protected closeMobileMenu(): void {
    this.isMobileMenuOpen.set(false);
  }

  protected readonly currentYear = new Date().getFullYear();

  /**
   * Admin-curated "Top Stores" from the home page settings, reused as the footer's Popular Stores
   * column. Empty until they load, and empty is meaningful: the column is not rendered at all rather
   * than showing placeholder brands that link nowhere (which is what it did before).
   */
  private readonly footerStoreLinks = signal<FooterLink[]>([]);

  /**
   * Header "Collections" menu: the deals categories, richest first. Loaded on first open (see
   * `loadDealCollections`) so a visitor who never uses the menu never pays for the request.
   */
  protected readonly dealCollections = signal<DealCollectionLink[]>([]);
  protected readonly isDealCollectionsLoading = signal(false);
  private dealCollectionsRequested = false;

  /**
   * Whether to show a Shop link at all — the master switch in admin > Shop Overview.
   *
   * Starts true and is corrected by `loadShopStatus`, which is the right way round: SSR waits for
   * that request before serialising the page, so the rendered HTML already carries the real answer
   * and the link never flickers in or out after hydration.
   */
  protected readonly shopEnabled = signal(true);

  private readonly consent = inject(ConsentService);

  /** Footer "Cookie settings" — the Cookie Policy tells readers this link exists. */
  protected reopenCookieSettings(): void {
    this.consent.reopen();
  }

  /**
   * The footer's link columns. Every entry is a route that exists in `app.routes.ts` — no `href="#"`
   * — so nothing here can rot into a dead link the way the previous hardcoded columns did.
   */
  /**
   * The legal row, kept out of `footerGroups` on purpose.
   *
   * These are not navigation. Nobody browsing for coupons is looking for the Cookie Policy, and
   * putting them in a link column both buried them and padded "Company & Help" to eleven entries,
   * past the point where any of it is scannable. A thin bar under the columns is where readers
   * already look for them.
   */
  protected readonly legalLinks = [
    { label: 'Terms of Service', path: '/terms' },
    { label: 'Privacy Policy', path: '/privacy-policy' },
    { label: 'Cookie Policy', path: '/cookie-policy' },
    { label: 'Affiliate Disclosure', path: '/affiliate-disclosure' },
    { label: 'Accessibility', path: '/accessibility' },
  ];

  protected readonly footerGroups = computed<FooterGroup[]>(() => {
    const stores = this.footerStoreLinks();
    const shop = this.shopEnabled();

    return [
      {
        label: 'Save More',
        links: [
          { label: "Today's Deals", path: '/collection' },
          { label: 'All Coupons', path: '/coupons' },
          { label: 'Categories', path: '/categories' },
          { label: 'All Stores', path: '/storelist' },
          { label: 'Events & Sales', path: '/events' },
          { label: 'Compare Stores', path: '/compare' },
          // Dropped entirely while the Shop is switched off — `/shop` 404s then, and the promise
          // this column makes is that every entry in it opens a real page.
          ...(shop ? [{ label: 'Shop', path: '/shop' }] : []),
        ],
      },
      {
        label: 'Coupons & Discounts',
        links: [
          { label: 'Verified Coupons', path: '/coupons/verified' },
          { label: 'Exclusive Coupons', path: '/coupons/exclusive' },
          { label: 'Free Shipping', path: '/coupons/free-shipping' },
          { label: 'Student Discounts', path: '/coupons/student-discount' },
          { label: 'Stores A–Z', path: '/storelist/a' },
        ],
      },
      {
        label: 'Guides & Blog',
        links: [
          { label: 'Blog Home', path: '/blogs' },
          { label: 'Blog Categories', path: '/blogs/categories' },
          { label: 'Topic Hubs', path: '/blogs/hubs' },
          { label: 'Our Experts', path: '/experts' },
        ],
      },
      {
        label: 'Company & Help',
        links: [
          { label: 'About Us', path: '/about-us' },
          { label: 'Contact Us', path: '/contact-us' },
          { label: 'Why Trust Us', path: '/why-trust-us' },
          { label: 'How Verification Works', path: '/how-verification-works' },
          { label: 'Submit a Coupon', path: '/submit-coupon' },
          { label: 'Add Your Brand', path: '/add-your-brand' },
        ],
      },
      ...(stores.length > 0 ? [{ label: 'Popular Stores', links: stores }] : []),
    ];
  });

  // Primary featured event (current, else next upcoming) — names the header "{Event} Coupons"
  // promo link. Empty until events load, in which case the link falls back to hardcoded text.
  protected readonly featuredEventLabel = signal('');
  protected readonly featuredEventPath = signal<string | null>(null);
  /** Label for the deals link — may name a different event than the coupons link (see the service). */
  protected readonly featuredEventDealsLabel = signal('');
  /** null when no event has a deals page — the header then renders no deals link. */
  protected readonly featuredEventDealsPath = signal<string | null>(null);
  /** "<Event> on Amazon", shown with the external Amazon affiliate link; '' until events load. */
  protected readonly featuredEventAmazonLabel = signal('');
  protected readonly featuredEventAmazonUrl = signal<string | null>(null);

  constructor() {
    // Every click-out goes through `/go/…` on the API origin — warm that connection while the
    // page loads rather than at click time.
    inject(PreconnectService).warm(this.apiConfig.assetBaseUrl);
    this.loadFeaturedEvent();
    this.loadFooterStores();
    this.loadShopStatus();

    // Live search for the header bar. Debounced so we query only after the user pauses, and
    // switchMap cancels stale in-flight requests.
    //
    // Uses /search rather than /stores: the endpoint already returns categories and the collections
    // behind them alongside the stores, and it is served from a 60-second cache, which the store
    // listing is not — typeahead traffic repeats heavily.
    this.searchInput$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((term) => {
          const query = term.trim();

          if (query.length < SEARCH_MIN_LENGTH) {
            this.isSearching.set(false);
            return of<SearchResultGroup[]>([]);
          }

          this.isSearching.set(true);

          return this.api.search(query).pipe(
            map((response) => this.toSearchGroups(response, query)),
            catchError(() => of<SearchResultGroup[]>([])),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((groups) => {
        this.searchGroups.set(groups);
        this.isSearching.set(false);
      });
  }

  /**
   * Arranges a search response into the dropdown's groups.
   *
   * Stores lead because that is what most searches are for. Categories and collections follow: the
   * same record can appear in both, and that is deliberate — /categories/<slug> lists its coupons
   * and /collection/<slug> lists its products, so which one a searcher wants depends on what they
   * came for. Empty groups are dropped rather than rendered as headings with nothing under them.
   */
  private toSearchGroups(
    response: ApiResponse<FrontendSearchResults>,
    query: string,
  ): SearchResultGroup[] {
    if (!response?.success || !response.data) return [];
    const data = response.data;

    const groups: SearchResultGroup[] = [
      {
        label: 'Stores',
        kind: 'Coupons & deals',
        // Reuses the existing mapper, which drops the duplicate brand records this catalogue
        // carries so the same store is not listed twice.
        results: this.toStoreResults(data.stores ?? [], query),
      },
      {
        label: 'Categories',
        kind: 'Coupons by category',
        results: (data.categories ?? []).slice(0, SEARCH_GROUP_LIMIT).map((category) => ({
          name: category.name,
          path: `/categories/${category.slug}`,
          logoUrl: category.iconImagePath ? this.assetUrl(category.iconImagePath) : '',
        })),
      },
      {
        label: 'Collections',
        kind: 'Products & deals',
        results: (data.collections ?? []).slice(0, SEARCH_GROUP_LIMIT).map((collection) => ({
          name: collection.name,
          path: `/collection/${collection.slug}`,
          logoUrl: collection.iconImagePath ? this.assetUrl(collection.iconImagePath) : '',
        })),
      },
    ];

    return groups.filter((group) => group.results.length > 0);
  }

  protected onSearchInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;

    this.searchTerm.set(value);
    this.isSearchOpen.set(value.trim().length >= SEARCH_MIN_LENGTH);
    this.searchInput$.next(value);
  }

  protected onSearchFocus(): void {
    if (this.searchTerm().trim().length >= SEARCH_MIN_LENGTH) {
      this.isSearchOpen.set(true);
    }
  }

  protected onSearchBlur(): void {
    // Backstop only: clicking a result no longer blurs the input, because the results panel
    // cancels its own mousedown. This just closes the panel when focus genuinely leaves.
    setTimeout(() => this.isSearchOpen.set(false), 150);
  }

  protected onSearchSubmit(event: Event): void {
    event.preventDefault();

    const [firstResult] = this.searchResults();

    if (firstResult && firstResult.path !== '#') {
      this.selectResult(firstResult.path);
    }
  }

  /**
   * Enter key only. The rows in the panel are real `routerLink` anchors and navigate themselves,
   * so they must NOT come through here — routing the same URL twice made the first click look
   * like it had done nothing.
   */
  protected selectResult(path: string): void {
    this.closeSearch();
    this.router.navigateByUrl(path);
  }

  /** A result row was clicked. `routerLink` handles the navigation; this only clears the UI. */
  protected onResultClick(): void {
    this.closeSearch();
  }

  private closeSearch(): void {
    this.isSearchOpen.set(false);
    // Leaving the term behind meant reopening the box showed a stale query against a page you
    // had already navigated to.
    this.searchTerm.set('');
    this.searchGroups.set([]);
  }

  private toStoreResults(stores: FrontendStore[], query: string): StoreSearchResult[] {
    const normalizedQuery = query.toLowerCase();
    const seen = new Set<string>();
    const results: StoreSearchResult[] = [];

    for (const store of stores) {
      // Keep name matches only (the API also matches domain/country) and drop the DB's
      // duplicate brand records so the same store isn't listed twice.
      if (!store.name.toLowerCase().includes(normalizedQuery)) {
        continue;
      }

      const key = (store.domain?.trim() || store.name).trim().toLowerCase();

      if (!key || seen.has(key)) {
        continue;
      }

      seen.add(key);

      const slug = store.slug?.trim() ?? '';
      const logoPath = store.logoImagePath?.trim() ?? '';

      results.push({
        name: store.name,
        path: slug ? `/${slug}` : '#',
        logoUrl: logoPath ? this.assetUrl(logoPath) : '',
      });

      if (results.length >= SEARCH_RESULT_LIMIT) {
        break;
      }
    }

    return results;
  }

  private assetUrl(path: string): string {
    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const base = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
    return `${base}${path.startsWith('/') ? path : `/${path}`}`;
  }

  /**
   * The header's two event promo links. Resolved by FeaturedEventService, which the BIG SALE banner
   * shares — this component used to carry its own copy of the event-picking logic, and the two could
   * disagree about which event was featured.
   */
  private loadFeaturedEvent(): void {
    this.featuredEvent
      .featured()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((links) => {
        this.featuredEventLabel.set(links?.couponLabel ?? '');
        this.featuredEventPath.set(links?.couponPath || null);
        this.featuredEventDealsLabel.set(links?.dealsLabel ?? '');
        this.featuredEventDealsPath.set(links?.dealsPath || null);
        this.featuredEventAmazonLabel.set(links?.amazonLabel ?? '');
        this.featuredEventAmazonUrl.set(links?.amazonUrl || null);
      });
  }

  /**
   * Loads the Collections menu on first hover/focus, guarded by `dealCollectionsRequested` so it
   * runs at most once per page — the same lazy pattern as the Site Map menu.
   *
   * Fails soft: on error the menu shows its empty state instead of blanking the header.
   */
  protected loadDealCollections(): void {
    if (this.dealCollectionsRequested) return;
    this.dealCollectionsRequested = true;
    this.isDealCollectionsLoading.set(true);

    this.api
      .getDealsCategories(DEAL_COLLECTION_LIMIT)
      .pipe(
        map((response) => response.data ?? []),
        catchError(() => of<FrontendDealCategoryListItem[]>([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((categories) => {
        const origin = this.apiConfig.assetBaseUrl.replace(/\/$/, '');
        const toUrl = (path: string) =>
          !path ? '' : /^https?:\/\//i.test(path) ? path : `${origin}${path}`;

        this.dealCollections.set(
          categories
            .filter((category) => category?.slug && category?.name)
            .map((category) => ({
              label: category.name,
              path: `/collection/${category.slug}`,
              productCount: category.productCount ?? 0,
              // The category icon when the admin set one, else the newest product's image.
              imageUrl: toUrl(category.iconImagePath || category.productImagePath || ''),
            })),
        );
        this.isDealCollectionsLoading.set(false);
      });
  }

  /**
   * Fills the footer's Popular Stores column from the home page's admin-picked Top Stores, so the
   * brands there are the ones the admin is actually promoting and their links always resolve.
   *
   * Fails soft: on an error, or when the admin has picked none, the column simply does not render.
   */
  private loadFooterStores(): void {
    this.api
      .getHome()
      .pipe(
        map((response) => response.data?.topStores ?? []),
        catchError(() => of<FrontendStore[]>([])),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((stores) => {
        const links: FooterLink[] = [];
        const seen = new Set<string>();

        for (const store of stores) {
          const slug = String(store?.slug ?? '').trim();
          const label = String(store?.name ?? '').trim();
          // The same brand can exist as several region/network records — one row each would list
          // the name repeatedly.
          if (!slug || !label || seen.has(slug)) continue;

          seen.add(slug);
          links.push({ label, path: `/${slug}` });
          if (links.length >= FOOTER_STORE_LIMIT) break;
        }

        this.footerStoreLinks.set(links);
      });
  }

  /**
   * The Shop's master switch (admin > Shop Overview).
   *
   * The header and footer are on every page, so this is what decides whether the site advertises a
   * Shop at all. The routes are guarded separately (`shopEnabledGuard`) — hiding a link is not the
   * same as closing a URL, and a switched-off Shop has to do both.
   *
   * On error the link stays: an unreachable settings endpoint means the backend is down, which is
   * not a reason to strip navigation off every page.
   */
  private loadShopStatus(): void {
    this.api
      .getShopSettings()
      .pipe(
        map((response) => response.data?.enabled !== false),
        catchError(() => of(true)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((enabled) => this.shopEnabled.set(enabled));
  }
}
